import { RequestModuleEnum } from 'shared/enums/request-module-enum'
import { useControlledSwr } from 'shared/hooks/use-controlled-swr'
import { useFetch } from 'shared/hooks/use-fetch'
import { RequestMethodsEnum } from 'shared/hooks/use-fetch/types'
import useUser from 'shared/hooks/use-user'
import { DataWithPaginationInterface } from 'shared/types/pagination-interface'
import { GOOGLE_INTEGRATIONS_API } from '../api'
import { IntegrationInterface, IntegrationUseCaseEnum } from '../types'

export const useGoogleIntegrations = () => {
  const { user } = useUser()
  const { fetcher: fetchIntegrations } = useFetch<
    RequestMethodsEnum.get,
    DataWithPaginationInterface<IntegrationInterface>
  >({
    method: RequestMethodsEnum.get,
  })

  const { fetcher: removeIntegrationFetcher } = useFetch<RequestMethodsEnum.delete>({
    method: RequestMethodsEnum.delete,
  })

  const { data, mutate, isLoading } = useControlledSwr(
    () => {
      if (!user) return
      return [GOOGLE_INTEGRATIONS_API, RequestModuleEnum.GoogleIntegrations]
    },
    fetchIntegrations,
    undefined,
  )

  const removeIntegration = async (id: IntegrationInterface['id']) => {
    await removeIntegrationFetcher(`${GOOGLE_INTEGRATIONS_API}/${id}`)
  }

  const getIntegrationsByUseCase = (useCase: IntegrationUseCaseEnum) => {
    if (!data) return

    return {
      ...data,
      items: data.items.filter(e => e.useCases.includes(useCase)),
    }
  }

  const isGoogleCalendarIntegrated = (() => {
    return getIntegrationsByUseCase(IntegrationUseCaseEnum.Calendar)?.items.length !== 0
  })()

  const mutateIntegrationsWithNewItem = async (item: IntegrationInterface) => {
    await mutate(data => {
      if (!data) return

      let newItems = data.items
      if (data.items.find(e => e.id === item.id)) {
        newItems = newItems.map(e => (e.id === item.id ? item : e))
      } else {
        newItems = [item, ...newItems]
      }

      return {
        ...data,
        items: newItems,
      }
    }, false)
  }

  return {
    isLoading,
    isGoogleCalendarIntegrated,
    getIntegrationsByUseCase,

    mutate,
    removeIntegration,
    mutateIntegrationsWithNewItem,
  }
}
